<template>
  <b-modal
    v-model="showModal"
    :title="isEditMode ? 'Edit Product Minimum Quantity' : 'Add Product Minimum Quantity'"
    size="md"
    no-close-on-backdrop
    @hidden="$emit('modal-closed')"
    @ok="submitHandler"
  >
    <b-form @submit.prevent="submitHandler">
      <b-row>
        <!-- Error Alert -->
        <b-col cols="12">
          <b-alert
            variant="danger"
            :show="errorMessage ? true : false"
          >
            <div
              v-if="typeof errorMessage === 'string'"
              class="alert-body"
            >
              <p>{{ errorMessage }}</p>
            </div>
            <div
              v-else-if="errorMessage.detail"
              class="alert-body"
            >
              <p>{{ errorMessage.detail }}</p>
            </div>
            <div
              v-else-if="errorMessage.message"
              class="alert-body"
            >
              <p>{{ errorMessage.message }}</p>
            </div>
          </b-alert>
        </b-col>

        <!-- Product Selection -->
        <b-col cols="12">
          <b-form-group>
            <template #label>
              Product
              <span class="text-danger">*</span>
            </template>
            <custom-v-select
              v-model="selectedProduct"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="filterProductOptions"
              label="display_text"
              placeholder="Search product..."
              :close-on-select="true"
              :deselect-from-dropdown="true"
              @search="onProductSearch"
              @input="handleProductChange"
            />
            <p
              v-if="validationErrors.product"
              class="error-message"
            >
              {{ validationErrors.product }}
            </p>
            <p
              v-if="errorMessage && errorMessage.code"
              class="error-message"
            >
              {{ errorMessage.code[0] }}
            </p>
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          class="mb-1"
        > <span v-if="selectedProduct">Lot : {{ selectedProduct.lot }}</span>
        </b-col>
        <!-- Minimum Order Quantity -->
        <b-col cols="12">
          <b-form-group>
            <template #label>
              Minimum Order Quantity
              <span class="text-danger">*</span>
            </template>
            <b-form-input
              v-model="minimumOrderQuantity"
              type="number"
              min="1"
              max="99999"
              placeholder="Enter minimum order quantity (max 5 digits)"
              :class="{ 'error-border': validationErrors.minimumOrderQuantity }"
              @input="handleMinQtyChange"
            />
            <p
              v-if="validationErrors.minimumOrderQuantity"
              class="error-message"
            >
              {{ validationErrors.minimumOrderQuantity }}
            </p>
            <p
              v-if="errorMessage && errorMessage.minimum_order_quantity"
              class="error-message"
            >
              {{ errorMessage.minimum_order_quantity[0] }}
            </p>
          </b-form-group>
        </b-col>

        <!-- Applicable Switch -->
        <b-col cols="12">
          <b-form-group>
            <template #label>
              Status
            </template>
            <div>
              <b-form-checkbox
                v-model="minimumOrderQuantityApplicable"
                switch
                size="lg"
              >
                {{ minimumOrderQuantityApplicable ? 'Active' : 'Inactive' }}
              </b-form-checkbox>
            </div>
          </b-form-group>
        </b-col>

      </b-row>
    </b-form>

    <template #modal-footer="{ ok, cancel }">
      <b-button
        variant="secondary"
        @click="cancel()"
      >
        Cancel
      </b-button>
      <b-button
        variant="primary"
        :disabled="saving || loading"
        @click="ok()"
      >
        {{ isEditMode ? 'Update' : 'Create' }}
        <b-spinner
          v-if="saving || loading"
          small
          label="Small Spinner"
        />
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import {
  BModal, BRow, BCol, BFormGroup, BButton, BFormInput,
  BForm, BSpinner, BAlert, BFormCheckbox,
} from 'bootstrap-vue'
import CustomVSelect from '@/components/UI/CustomVSelect.vue'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import getParsedQueryString from '@/utils/headerQueryParser'

export default {
  components: {
    CustomVSelect,
    BModal,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BForm,
    BButton,
    BSpinner,
    BAlert,
    BFormCheckbox,
  },

  props: {
    id: {
      type: [String, Number],
      default: null,
    },
  },

  data() {
    return {
      showModal: true,
      loading: false,
      minimumOrderQuantity: '',
      minimumOrderQuantityApplicable: true,
      products: [],
      selectedProduct: null,
      productOptions: [],
      filterProductOptions: [],
      saving: false,
      errorMessage: {},
      validationErrors: {},
      searchTimeout: null,
    }
  },

  computed: {
    isEditMode() {
      return !!(this.id)
    },
  },

  async created() {
    await this.fetchProducts()
    if (this.isEditMode) {
      this.fetchDetails()
    }
  },

  beforeDestroy() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
  },

  methods: {
    onProductSearch(search) {
      if (this.productSearchTimeout) {
        clearTimeout(this.productSearchTimeout)
      }

      this.productSearchTimeout = setTimeout(() => {
        this.filterProducts(search)
      }, 300)
    },
    filterProducts(search) {
      if (!search || search.trim() === '') {
        // Show only first 15 items when no search
        this.filterProductOptions = this.productOptions.slice(0, 15)
      } else {
        // Filter products by search term
        const searchLower = search.toLowerCase()
        const filtered = this.productOptions.filter(product => product.name.toLowerCase().includes(searchLower)
          || (product.code && product.code.toLowerCase().includes(searchLower)))

        // Limit to first 50 results for performance
        this.filterProductOptions = filtered.slice(0, 50)
      }
    },

    async fetchProducts() {
      try {
        const productRes = await axios.get('/marg/product_options')
        this.productOptions = productRes.data.data.map(product => ({
          id: product.code,
          code: product.code,
          name: product.name,
          lot: product.lot,
          display_text: product.name,
        }))

        // Initialize with first 15 products
        this.filterProductOptions = this.productOptions.slice(0, 15)
      } catch (error) {
        this.errors = error.response?.data?.error || 'Failed to fetch products. Please try again.'
      }
    },

    setProductByCode(code) {
      const matchedProduct = this.productOptions.find(product => product.code === code)
      if (matchedProduct) {
        this.selectedProduct = matchedProduct
      }
    },

    fetchDetails() {
      this.loading = true
      axios.get(`/marg/product_controls/${this.id}/`)
        .then(res => {
          const { data } = res

          // Set minimum order quantity and applicable status
          this.minimumOrderQuantity = data.minimum_order_quantity || ''
          this.minimumOrderQuantityApplicable = data.minimum_order_quantity_applicable !== undefined
            ? data.minimum_order_quantity_applicable : true
          if (data.code) {
            const foundProduct = this.productOptions.find(p => p.code === data.code)
            if (foundProduct) {
              this.selectedProduct = foundProduct
              // Ensure the selected product is in the filtered options
              if (!this.filterProductOptions.find(p => p.code === data.code)) {
                this.filterProductOptions = [foundProduct, ...this.filterProductOptions.slice(0, 14)]
              }
            } else {
              this.selectedProduct = null
            }
          } else {
            this.selectedProduct = null
          }
          this.loading = false
        })
        .catch(error => {
          this.errorMessage = error?.response?.data || 'Failed to fetch details'
          this.loading = false
        })
    },

    searchProductsByName(productName) {
      this.productLoading = true

      const params = {
        page: 1,
        per_page: 60,
        name: productName,
      }

      axios.get(`/marg/products?${getParsedQueryString(params)}`)
        .then(res => {
          this.productOptions = res.data && res.data.results.map(product => ({
            id: product.code,
            code: product.code,
            name: product.name,
            lot: product.lot,
            display_text: `${product.name}`,
          }))
          this.productLoading = false
          this.initialLoadDone = true

          // Try to match the product by code
          if (this.pendingProductCode) {
            this.setProductByCode(this.pendingProductCode)
            this.pendingProductCode = null
          }
        })
        .catch(error => {
          this.errorMessage = error.message || 'Something went wrong'
          this.productOptions = []
          this.productLoading = false
          this.initialLoadDone = true
        })
    },

    handleProductChange() {
      this.clearValidationError('product')
    },

    handleMinQtyChange() {
      this.clearValidationError('minimumOrderQuantity')
    },

    clearValidationError(field) {
      if (this.validationErrors[field]) {
        this.$delete(this.validationErrors, field)
      }
    },

    validateForm() {
      this.validationErrors = {}
      let isValid = true

      // Validate product
      if (!this.selectedProduct) {
        this.validationErrors.product = 'Product is required'
        isValid = false
      }

      // Validate minimum order quantity
      const minQty = this.minimumOrderQuantity && this.minimumOrderQuantity.toString().trim()
      if (!minQty) {
        this.validationErrors.minimumOrderQuantity = 'Minimum order quantity is required'
        isValid = false
      } else if (!/^\d{1,5}$/.test(minQty)) {
        this.validationErrors.minimumOrderQuantity = 'Must be an integer with maximum 5 digits'
        isValid = false
      } else {
        const quantity = parseInt(minQty, 10)
        if (quantity <= 0) {
          this.validationErrors.minimumOrderQuantity = 'Must be greater than 0'
          isValid = false
        }
      }

      return isValid
    },

    submitHandler(event) {
      event.preventDefault()
      this.errorMessage = {}

      if (!this.validateForm()) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Validation Error',
            text: 'Please fix the errors and try again',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
        return
      }

      const payload = {
        code: this.selectedProduct.code,
        minimum_order_quantity: parseInt(this.minimumOrderQuantity, 10),
        minimum_order_quantity_applicable: this.minimumOrderQuantityApplicable,
      }

      this.saving = true

      const request = this.isEditMode
        ? axios.patch(`/marg/product_controls/${this.id}/`, payload)
        : axios.post('/marg/product_controls/', payload)

      request
        .then(res => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: res.data.message || (this.isEditMode ? 'Updated Successfully' : 'Created Successfully'),
              icon: 'CheckIcon',
              variant: 'success',
            },
          })

          this.$emit(this.isEditMode ? 'minimum-quantity-updated' : 'minimum-quantity-added')
          this.saving = false
          this.showModal = false
        })
        .catch(error => {
          this.errorMessage = error?.response?.data?.error || error?.response?.data || 'Something went wrong'
          this.saving = false
        })
    },
  },
}
</script>

<style scoped>
.error-message {
  color: red;
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

.error-border {
  border-color: #dc3545 !important;
}

.error-border:focus {
  border-color: #dc3545 !important;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important;
}

.text-danger {
  color: #dc3545 !important;
}

input::placeholder {
  color: #6c757d !important;
  opacity: 1;
}

.vs__dropdown-toggle .vs__selected-options .vs__search::placeholder {
  color: #6c757d !important;
}
</style>
